import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/data/products";
import { easing } from "@/lib/animations";

const STEPS = [
    { id: "shipping", label: "Shipping" },
    { id: "payment", label: "Payment" },
    { id: "review", label: "Review" },
];

const Field = ({ label, name, value, onChange, type = "text", required, testId, full }) => (
    <label
        style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            gridColumn: full ? "1 / -1" : "auto",
        }}
    >
        <span
            style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 10,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.55)",
            }}
        >
            {label} {required && <span style={{ color: "#c1121f" }}>*</span>}
        </span>
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            data-testid={testId}
            style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.25)",
                color: "#fff",
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 14,
                padding: "12px 14px",
                letterSpacing: "0.02em",
                outline: "none",
                transition: "border-color 0.3s",
            }}
            onFocus={(e) =>
                (e.currentTarget.style.borderColor = "rgba(255,255,255,0.65)")
            }
            onBlur={(e) =>
                (e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)")
            }
        />
    </label>
);

const Stepper = ({ stepIdx }) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            flexWrap: "wrap",
            marginBottom: 32,
        }}
        data-testid="checkout-stepper"
    >
        {STEPS.map((s, i) => {
            const active = i === stepIdx;
            const done = i < stepIdx;
            return (
                <React.Fragment key={s.id}>
                    <span
                        data-testid={`checkout-step-${s.id}`}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 10,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            color: active
                                ? "#fff"
                                : done
                                  ? "rgba(255,255,255,0.55)"
                                  : "rgba(255,255,255,0.3)",
                        }}
                    >
                        <span
                            style={{
                                width: 22,
                                height: 22,
                                border: "1px solid currentColor",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: done ? "#fff" : "transparent",
                                color: done ? "#0c0c0c" : "inherit",
                                fontSize: 10,
                            }}
                        >
                            {done ? "✓" : String(i + 1).padStart(2, "0")}
                        </span>
                        {s.label}
                    </span>
                    {i < STEPS.length - 1 && (
                        <span
                            aria-hidden="true"
                            style={{
                                width: 32,
                                height: 1,
                                background: "rgba(255,255,255,0.25)",
                            }}
                        />
                    )}
                </React.Fragment>
            );
        })}
    </div>
);

export default function Checkout() {
    const { items, count, subtotal, clearCart } = useCart();
    const navigate = useNavigate();
    const [stepIdx, setStepIdx] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        email: "",
        firstName: "",
        lastName: "",
        address: "",
        city: "",
        postal: "",
        country: "Poland",
        phone: "",
        cardName: "",
        cardNumber: "",
        cardExpiry: "",
        cardCvc: "",
    });

    const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const SHIPPING_EUR = subtotal >= 200 ? 0 : 12;
    const total = useMemo(() => subtotal + SHIPPING_EUR, [subtotal, SHIPPING_EUR]);

    if (items.length === 0 && !submitted) {
        return (
            <PageShell
                sectionNumber="05"
                eyebrow="Empty signal"
                title="Checkout"
                crumb="Checkout"
                data-testid="page-checkout"
            >
                <p
                    style={{
                        margin: 0,
                        fontFamily: "'EB Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 19,
                        color: "rgba(255,255,255,0.65)",
                        maxWidth: "44ch",
                        marginBottom: 28,
                    }}
                >
                    Nothing to check out. Wander the Lookbook first.
                </p>
                <Link
                    to="/shop"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                        color: "#fff",
                        textDecoration: "none",
                        border: "1px solid rgba(255,255,255,0.45)",
                        padding: "14px 26px",
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 11,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                    }}
                >
                    Enter Lookbook
                </Link>
            </PageShell>
        );
    }

    if (submitted) {
        return (
            <PageShell
                sectionNumber="05"
                eyebrow="Signal received"
                title="Order Placed"
                crumb="Confirmation"
                data-testid="page-checkout-success"
            >
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: easing.primary }}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 28,
                        maxWidth: 640,
                    }}
                    data-testid="checkout-success"
                >
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "'EB Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 21,
                            color: "rgba(255,255,255,0.78)",
                            lineHeight: 1.5,
                        }}
                    >
                        Your order will be cut, packed and dispatched within
                        five business days. A confirmation has been sent to{" "}
                        <span style={{ color: "#fff" }}>{form.email}</span>.
                    </p>
                    <div
                        style={{
                            padding: "22px 24px",
                            border: "1px solid rgba(255,255,255,0.18)",
                            background: "rgba(255,255,255,0.04)",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.75)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Order ID</span>
                            <span style={{ color: "#fff" }}>
                                PRF-{Math.random().toString(36).slice(2, 8).toUpperCase()}
                            </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Total paid</span>
                            <span style={{ color: "#fff" }}>{formatPrice(total)}</span>
                        </div>
                    </div>
                    <Link
                        to="/"
                        data-testid="checkout-success-home"
                        style={{
                            color: "#fff",
                            textDecoration: "none",
                            border: "1px solid rgba(255,255,255,0.45)",
                            padding: "14px 26px",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            alignSelf: "flex-start",
                        }}
                    >
                        Return to Home
                    </Link>
                </motion.div>
            </PageShell>
        );
    }

    const goNext = (e) => {
        e.preventDefault();
        if (stepIdx < STEPS.length - 1) {
            setStepIdx((s) => s + 1);
        } else {
            setSubmitted(true);
            clearCart();
        }
    };

    const goPrev = () => stepIdx > 0 && setStepIdx((s) => s - 1);

    return (
        <PageShell
            sectionNumber="05"
            eyebrow={`${String(count).padStart(2, "0")} items · ${formatPrice(total)}`}
            title="Checkout"
            crumb="Checkout"
            data-testid="page-checkout"
        >
            <div className="proof-grid-main">
                {/* FORM COLUMN */}
                <form
                    onSubmit={goNext}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 24,
                    }}
                    data-testid="checkout-form"
                >
                    <Stepper stepIdx={stepIdx} />

                    <AnimatePresence mode="wait">
                        {stepIdx === 0 && (
                            <motion.div
                                key="shipping"
                                initial={{ opacity: 0, x: 18 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -12 }}
                                transition={{ duration: 0.45, ease: easing.primary }}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(220px, 1fr))",
                                    gap: 18,
                                }}
                                data-testid="checkout-shipping"
                            >
                                <Field
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={update}
                                    required
                                    full
                                    testId="checkout-email"
                                />
                                <Field
                                    label="First name"
                                    name="firstName"
                                    value={form.firstName}
                                    onChange={update}
                                    required
                                    testId="checkout-firstname"
                                />
                                <Field
                                    label="Last name"
                                    name="lastName"
                                    value={form.lastName}
                                    onChange={update}
                                    required
                                    testId="checkout-lastname"
                                />
                                <Field
                                    label="Address"
                                    name="address"
                                    value={form.address}
                                    onChange={update}
                                    required
                                    full
                                    testId="checkout-address"
                                />
                                <Field
                                    label="City"
                                    name="city"
                                    value={form.city}
                                    onChange={update}
                                    required
                                    testId="checkout-city"
                                />
                                <Field
                                    label="Postal code"
                                    name="postal"
                                    value={form.postal}
                                    onChange={update}
                                    required
                                    testId="checkout-postal"
                                />
                                <Field
                                    label="Country"
                                    name="country"
                                    value={form.country}
                                    onChange={update}
                                    required
                                    testId="checkout-country"
                                />
                                <Field
                                    label="Phone"
                                    name="phone"
                                    value={form.phone}
                                    onChange={update}
                                    testId="checkout-phone"
                                />
                            </motion.div>
                        )}

                        {stepIdx === 1 && (
                            <motion.div
                                key="payment"
                                initial={{ opacity: 0, x: 18 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -12 }}
                                transition={{ duration: 0.45, ease: easing.primary }}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(220px, 1fr))",
                                    gap: 18,
                                }}
                                data-testid="checkout-payment"
                            >
                                <Field
                                    label="Cardholder name"
                                    name="cardName"
                                    value={form.cardName}
                                    onChange={update}
                                    required
                                    full
                                    testId="checkout-card-name"
                                />
                                <Field
                                    label="Card number"
                                    name="cardNumber"
                                    value={form.cardNumber}
                                    onChange={update}
                                    required
                                    full
                                    testId="checkout-card-number"
                                />
                                <Field
                                    label="Expiry (MM/YY)"
                                    name="cardExpiry"
                                    value={form.cardExpiry}
                                    onChange={update}
                                    required
                                    testId="checkout-card-expiry"
                                />
                                <Field
                                    label="CVC"
                                    name="cardCvc"
                                    value={form.cardCvc}
                                    onChange={update}
                                    required
                                    testId="checkout-card-cvc"
                                />
                                <p
                                    style={{
                                        gridColumn: "1 / -1",
                                        margin: 0,
                                        fontFamily: "'EB Garamond', serif",
                                        fontStyle: "italic",
                                        fontSize: 14,
                                        color: "rgba(255,255,255,0.5)",
                                        lineHeight: 1.55,
                                    }}
                                >
                                    Final checkout will be handled by Shopify in
                                    production. Test payment fields are placeholders.
                                </p>
                            </motion.div>
                        )}

                        {stepIdx === 2 && (
                            <motion.div
                                key="review"
                                initial={{ opacity: 0, x: 18 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -12 }}
                                transition={{ duration: 0.45, ease: easing.primary }}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 22,
                                }}
                                data-testid="checkout-review"
                            >
                                <div
                                    style={{
                                        padding: "20px 22px",
                                        border:
                                            "1px solid rgba(255,255,255,0.18)",
                                        background: "rgba(255,255,255,0.04)",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontFamily:
                                                "JetBrains Mono, monospace",
                                            fontSize: 10,
                                            letterSpacing: "0.28em",
                                            textTransform: "uppercase",
                                            color: "rgba(255,255,255,0.5)",
                                        }}
                                    >
                                        Ship to
                                    </span>
                                    <span
                                        style={{
                                            fontFamily:
                                                "'Space Grotesk', sans-serif",
                                            fontSize: 15,
                                            color: "#fff",
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        {form.firstName} {form.lastName}
                                        <br />
                                        {form.address}
                                        <br />
                                        {form.postal} {form.city}, {form.country}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        padding: "20px 22px",
                                        border:
                                            "1px solid rgba(255,255,255,0.18)",
                                        background: "rgba(255,255,255,0.04)",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontFamily:
                                                "JetBrains Mono, monospace",
                                            fontSize: 10,
                                            letterSpacing: "0.28em",
                                            textTransform: "uppercase",
                                            color: "rgba(255,255,255,0.5)",
                                        }}
                                    >
                                        Pay with
                                    </span>
                                    <span
                                        style={{
                                            fontFamily:
                                                "'Space Grotesk', sans-serif",
                                            fontSize: 15,
                                            color: "#fff",
                                        }}
                                    >
                                        Card · ••••{" "}
                                        {form.cardNumber.slice(-4) || "0000"}
                                    </span>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingTop: 22,
                            borderTop: "1px solid rgba(255,255,255,0.14)",
                            gap: 14,
                            flexWrap: "wrap",
                        }}
                    >
                        {stepIdx > 0 ? (
                            <button
                                type="button"
                                onClick={goPrev}
                                data-testid="checkout-prev"
                                style={{
                                    background: "transparent",
                                    color: "rgba(255,255,255,0.65)",
                                    border: "1px solid rgba(255,255,255,0.25)",
                                    fontFamily: "JetBrains Mono, monospace",
                                    fontSize: 11,
                                    letterSpacing: "0.28em",
                                    textTransform: "uppercase",
                                    padding: "14px 22px",
                                    cursor: "pointer",
                                }}
                            >
                                ← Back
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => navigate("/cart")}
                                data-testid="checkout-back-cart"
                                style={{
                                    background: "transparent",
                                    color: "rgba(255,255,255,0.55)",
                                    border: "none",
                                    fontFamily: "JetBrains Mono, monospace",
                                    fontSize: 11,
                                    letterSpacing: "0.28em",
                                    textTransform: "uppercase",
                                    cursor: "pointer",
                                    padding: 0,
                                }}
                            >
                                ← Edit cart
                            </button>
                        )}
                        <button
                            type="submit"
                            data-testid="checkout-next"
                            style={{
                                background: "#fff",
                                color: "#0c0c0c",
                                border: "none",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                padding: "14px 28px",
                                cursor: "pointer",
                            }}
                        >
                            {stepIdx === STEPS.length - 1
                                ? "Place Order →"
                                : "Continue →"}
                        </button>
                    </div>
                </form>

                {/* ORDER SUMMARY */}
                <aside
                    style={{
                        position: "sticky",
                        top: 120,
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        padding: "26px 24px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                    }}
                    data-testid="checkout-summary"
                >
                    <h3
                        style={{
                            margin: 0,
                            fontFamily: "'Space Grotesk', Manrope, sans-serif",
                            fontWeight: 700,
                            fontSize: 18,
                            letterSpacing: "-0.01em",
                            textTransform: "uppercase",
                        }}
                    >
                        Your Order
                    </h3>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                            maxHeight: 280,
                            overflowY: "auto",
                            paddingRight: 4,
                        }}
                    >
                        {items.map((it) => (
                            <div
                                key={`${it.id}-${it.size}`}
                                style={{
                                    display: "flex",
                                    gap: 12,
                                    fontFamily: "'Space Grotesk', sans-serif",
                                    fontSize: 13,
                                    color: "rgba(255,255,255,0.85)",
                                }}
                            >
                                <span
                                    style={{
                                        width: 48,
                                        height: 48,
                                        background: "#1a1a1a",
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <img
                                        src={it.img}
                                        alt=""
                                        style={{
                                            width: "78%",
                                            height: "78%",
                                            objectFit: "contain",
                                            mixBlendMode: "screen",
                                            filter: "brightness(1.1)",
                                        }}
                                    />
                                </span>
                                <div
                                    style={{
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 2,
                                    }}
                                >
                                    <span
                                        style={{
                                            textTransform: "uppercase",
                                            letterSpacing: "0.04em",
                                            fontSize: 12,
                                        }}
                                    >
                                        {it.name}
                                    </span>
                                    <span
                                        style={{
                                            fontFamily:
                                                "JetBrains Mono, monospace",
                                            fontSize: 10,
                                            letterSpacing: "0.22em",
                                            textTransform: "uppercase",
                                            color: "rgba(255,255,255,0.5)",
                                        }}
                                    >
                                        {it.size} · ×{it.qty}
                                    </span>
                                </div>
                                <span
                                    style={{
                                        fontFamily:
                                            "JetBrains Mono, monospace",
                                        fontSize: 11,
                                        letterSpacing: "0.14em",
                                        color: "rgba(255,255,255,0.75)",
                                    }}
                                >
                                    {formatPrice(it.priceEUR * it.qty)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div
                        style={{
                            paddingTop: 14,
                            borderTop: "1px solid rgba(255,255,255,0.18)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.14em",
                            color: "rgba(255,255,255,0.7)",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Subtotal</span>
                            <span data-testid="checkout-subtotal">
                                {formatPrice(subtotal)}
                            </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Shipping</span>
                            <span>
                                {SHIPPING_EUR === 0 ? "Free" : formatPrice(SHIPPING_EUR)}
                            </span>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: "#fff",
                                fontSize: 14,
                                letterSpacing: "0.18em",
                                paddingTop: 8,
                                borderTop: "1px solid rgba(255,255,255,0.12)",
                            }}
                        >
                            <span>Total</span>
                            <span data-testid="checkout-total">
                                {formatPrice(total)}
                            </span>
                        </div>
                    </div>
                </aside>
            </div>
        </PageShell>
    );
}
